import { useState, useEffect } from 'react';
import { Truck, Banknote, Play, X } from 'lucide-react';
import StalyPruh from './StalyPruh';
import ShiftCardCta from './ShiftCardCta';

const fmtTime = t => (t || '').toString().slice(0, 5);

const PHASE_COLOR = '#FF8A00';   // fáze zahájení

/** Tikající elapsed od zvoleného clock-in času (manualTimeOverride nebo cas_od).
 *  Anchor je local-time ISO bez TZ suffixu, JS Date ho parsuje jako lokální čas
 *  uživatele — to je správně, protože uživatel vybírá čas v lokální zóně.
 *  Když je anchor v budoucnu, ukáže "Začne za" countdown (kladně) — nesvádět
 *  uživatele zápornými minutami. Sekundový tick pro pocit "něco se děje". */
function useElapsedFromAnchor(datum, casHHMM) {
    const [data, setData] = useState({ label: '', smer: 'after' });
    useEffect(() => {
        if (!datum || !casHHMM) { setData({ label: '', smer: 'after' }); return; }
        const d = typeof datum === 'string' ? datum.slice(0, 10) : datum;
        const t = String(casHHMM).slice(0, 5);
        const anchor = new Date(`${d}T${t}:00`); // local time
        if (isNaN(anchor.getTime())) { setData({ label: '', smer: 'after' }); return; }
        const tick = () => {
            const diffSec = Math.floor((Date.now() - anchor.getTime()) / 1000);
            const sec = Math.abs(diffSec);
            const h = Math.floor(sec / 3600);
            const m = Math.floor((sec % 3600) / 60);
            const label = `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m`;
            setData({ label, smer: diffSec >= 0 ? 'after' : 'before' });
        };
        tick();
        // Tick každých 15 s — label se mění po minutě, ale `smer` může
        // překlopit přesněji při skoku přes start.
        const iv = setInterval(tick, 15000);
        return () => clearInterval(iv);
    }, [datum, casHHMM]);
    return data;
}

/**
 * Karta fáze "Zahájení" — sjednocená hlavička (✕ = zrušit zahájení),
 * elapsed slot, dlaždice auto/pokladna a CTA dokončující zahájení a clock-in.
 * ChecklistPanel patří pod tuto kartu (renderuje se v index.jsx).
 */
export default function ShiftCardStarting({
    smena, dochazka, tym = [],
    vozidloItem,
    pokladnaItem,
    manualTimeOverride = null,
    onOpenAutoPicker,
    onOpenPokladnaPicker,
    onDokoncit,
    onCancel,
    onOpenTeam,
    faze = [],
    aktivniFazeId = null,
    onOtevritFazi,
    canDokoncit = true,
    chybiKontrol = 0,                    // povinné položky fáze zahájení, které zbývají
    clockBusy, error,
    isDark, isMobile = false,
    children,                            // pracovní plocha pod pruhem
    plocha = null,                       // otevřená plocha dlaždice ('kolegove' | null)
}) {
    // Elapsed kotvíme na čas, který uživatel vybral (manualTimeOverride
    // z ClockInManualModalu), default cas_od.
    const planovanyStart = manualTimeOverride || (smena ? fmtTime(smena.cas_od) : '');
    const { label: elapsed, smer } = useElapsedFromAnchor(smena?.datum, planovanyStart);

    const pozadujeAuto = !!smena?.pozaduje_vozidlo;
    const pozadujePokladnu = !!smena?.pozaduje_pokladnu;
    // Manažer směny dostává hlavní pokladnu automaticky (backend ji přiřadí při
    // clock-inu) — nevybírá ji, slot je jen předvyplněný a nenutí potvrzení.
    const pokladnaAutoManazer = !!smena?.manazer_smeny && pozadujePokladnu;
    const slotyCelkem = (pozadujeAuto ? 1 : 0) + (pozadujePokladnu && !pokladnaAutoManazer ? 1 : 0);
    const slotyHotovo =
        (pozadujeAuto && vozidloItem ? 1 : 0) +
        (pozadujePokladnu && !pokladnaAutoManazer && pokladnaItem ? 1 : 0);
    const slotyOk = slotyCelkem === 0 || slotyHotovo === slotyCelkem;
    const muzePotvrdit = slotyOk && canDokoncit;

    // Zamčené tlačítko nese jméno překážky, ne akci, kterou nejde provést —
    // stejný vzor jako na konci směny. Chybějící auto a pokladna se počítají
    // spolu s kontrolami: pro toho, kdo chce začít, je to jeden seznam věcí
    // k doplnění, ne dvě různé kategorie. Jedna konkrétní překážka se ale
    // pojmenuje rovnou („Vyber auto") — je to užitečnější než počet.
    const chybiAuto = pozadujeAuto && !vozidloItem;
    const chybiPokladna = pozadujePokladnu && !pokladnaAutoManazer && !pokladnaItem;
    const chybiCelkem = (chybiAuto ? 1 : 0) + (chybiPokladna ? 1 : 0) + chybiKontrol;

    let ctaPopisek = 'Začít směnu';
    if (chybiCelkem > 0) {
        if (chybiKontrol === 0 && chybiAuto && !chybiPokladna) {
            ctaPopisek = 'Vyber auto';
        } else if (chybiKontrol === 0 && chybiPokladna && !chybiAuto) {
            ctaPopisek = 'Vyber pokladnu';
        } else if (!chybiAuto && !chybiPokladna) {
            ctaPopisek = chybiKontrol === 1 ? 'Zbývá 1 kontrola'
                : chybiKontrol < 5 ? `Zbývají ${chybiKontrol} kontroly`
                : `Zbývá ${chybiKontrol} kontrol`;
        } else {
            ctaPopisek = chybiCelkem === 1 ? 'Zbývá 1 věc'
                : chybiCelkem < 5 ? `Zbývají ${chybiCelkem} věci`
                : `Zbývá ${chybiCelkem} věcí`;
        }
    }

    if (!smena) return null;

    const sloty = [
        {
            id: 'auto',
            variant: !pozadujeAuto ? 'info' : (vozidloItem ? 'active' : 'action'),
            disabled: !pozadujeAuto,
            icon: <Truck size={14} />,
            label: 'Auto',
            value: pozadujeAuto ? (vozidloItem?.spz || 'Vyber') : null,
            onClick: pozadujeAuto ? onOpenAutoPicker : null,
            accent: '#3B82F6',
        },
        {
            id: 'pokladna',
            variant: !pozadujePokladnu ? 'info' : ((pokladnaAutoManazer || pokladnaItem) ? 'active' : 'action'),
            disabled: !pozadujePokladnu,
            icon: <Banknote size={14} />,
            label: 'Pokladna',
            value: pozadujePokladnu
                ? (pokladnaAutoManazer ? 'Hlavní' : (pokladnaItem?.nazev || 'Vyber'))
                : null,
            onClick: pozadujePokladnu && !pokladnaAutoManazer ? onOpenPokladnaPicker : null,
            accent: '#10B981',
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0 }}>
            {/* Název tu nestojí. Odpověď na „kterou dnes dělám" dá rozšířený
                detail před zahájením; jakmile je člověk uvnitř, je ten řádek
                jen daň z výšky, kterou potřebují checklisty a dlaždice. */}
            <StalyPruh
                barvaFaze={PHASE_COLOR}
                faze={faze}
                aktivniFazeId={aktivniFazeId}
                onOtevritFazi={onOtevritFazi}
                stred={elapsed ? `příprava ${elapsed}` : null}
                sloty={sloty}
                tym={tym}
                onTym={onOpenTeam}
                tymVybrano={plocha === 'kolegove'}
                isDark={isDark}
                isMobile={isMobile}
                akce={
                    <ShiftCardCta
                        /* Dřív „Dokončit zahájení a začít směnu" — vedle sekundárního
                           „Zrušit" se to neveslo a osekalo se to na „…začít s…".
                           Že jde o zahájení, teď stejně říká osa fází nad tím. */
                        label={ctaPopisek}
                        icon={<Play size={16} fill="currentColor" />}
                        onClick={onDokoncit}
                        disabled={!muzePotvrdit}
                        busy={clockBusy}
                        busyLabel="Zahajuji…"
                        barva={PHASE_COLOR}
                        /* „Zrušit přípravu" bývalo křížkem v hlavičce, která zmizela.
                           Je to ústupová akce, takže patří vedle té hlavní. */
                        secondary={onCancel ? { icon: <X size={13} />, label: 'Zrušit', onClick: onCancel, title: 'Zrušit přípravu a vrátit se' } : null}
                        isDark={isDark}
                    />
                }
            />

            {/* Chyba zahájení patří k tlačítku, které ji vyvolalo — to je
                teď hrana pruhu, takže stojí hned pod ní, ne pod checklistem. */}
            {error && (
                <div style={{
                    margin: isMobile ? '0 16px' : 0,
                    padding: '8px 12px', borderRadius: 7, fontSize: 12,
                    background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                    border: '1px solid rgba(239,68,68,0.3)',
                }}>{error}</div>
            )}

            {/* Pracovní plocha přijíždí zezdola místo aby naskočila. Navazuje
                na složení karty nad ní — checklist se tak objeví „pod" pruhem
                ve chvíli, kdy tam pro něj vzniklo místo. */}
            <style>{`
                @keyframes prichod-plochy {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: none; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .ms-prichod { animation: none !important; }
                }
            `}</style>
            <div className="ms-prichod" style={{
                flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
                animation: 'prichod-plochy 0.3s cubic-bezier(0.4, 0, 0.2, 1) both',
            }}>
                {children}
            </div>
        </div>
    );
}
